import { Button } from "components/buttons/button";
import { SingleBookSelector } from "components/forms/book-selector";
import { Input, inputClassNames, StandardInput } from "components/forms/input";
import { SingleProductSelector } from "components/forms/product-selector";
import {
  ShippingSelector,
  ShippingSelectorValue,
} from "components/forms/shipping-selector";
import { Dispatch } from "react";
import { LineItem, Action, itemTypes } from "pages/admin/orders/new";
import { UseInputReturn } from "lib/hooks/use-input";
import currency from "currency.js";

interface Props {
  lineItems: LineItem[];
  dispatch: Dispatch<Action>;
  fulfillment: UseInputReturn<ShippingSelectorValue>;
  country: UseInputReturn<string>;
  canAddItem: boolean;
}

export const OrderDetails = ({
  dispatch,
  fulfillment,
  lineItems,
  canAddItem,
  country,
}: Props) => {
  const calcTotal = (asInt = false) => {
    const lineItemsTotal = lineItems
      .map((item) => item.totalPrice(true))
      .reduce((acc, curr) => acc + curr, 0);

    const shippingTotal = fulfillment.value.price;

    if (asInt) {
      return lineItemsTotal + fulfillment.value.price;
    } else {
      return currency(lineItemsTotal + fulfillment.value.price, {
        fromCents: true,
      }).value;
    }
  };

  return (
    <fieldset className='col-span-full border-t flex flex-col space-y-8 border-gray-500'>
      <legend className='pr-4 text-gray-500 header text-xl'>
        Order Details
      </legend>
      <table className='divide-y w-full divide-gray-200 shadow border-b border-gray-200 sm:rounded-lg '>
        <thead className=' bg-gray-100 text-left text-xs leading-4 font-medium text-gray-700 uppercase tracking-wider table-fixed'>
          <tr>
            <th className='px-2 xl:px-6 py-3 w-3/12'>Item Type</th>
            <th className='px-2 xl:px-6 py-3 w-4/12'>Item</th>
            <th className='px-2 xl:px-6 py-3 w-2/12'>Price</th>
            <th className='px-2 xl:px-6 py-3 w-1/12'>Quantity</th>
            <th className='px-2 xl:px-6 py-3 w-1/12'>Discount</th>
            <th className='px-2 xl:px-6 py-3 w-1/12'>Total</th>
            <th className='px-2 xl:px-6 py-3 w-1/12'></th>
          </tr>
        </thead>
        <tbody className='bg-white divide-y divide-gray-200 text-sm text-gray-800'>
          <tr className=''>
            <td
              className='px-2 xl:px-6 py-2 whitespace-no-wrap leading-5 '
              colSpan={7}>
              <Button
                buttonType='ghost'
                buttonSize='sm'
                type='button'
                disabled={!canAddItem}
                onClick={() => dispatch({ type: "addItem" })}>
                <svg
                  className='w-3 h-3 mr-2'
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 20 20'
                  fill='currentColor'>
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z'
                    clipRule='evenodd'
                  />
                </svg>
                Add Item
              </Button>
            </td>
          </tr>
          {lineItems.map((item, index) => (
            <tr key={`${index}_lineItem`}>
              <td className='px-2 xl:px-6 py-2 whitespace-no-wrap leading-5'>
                <select
                  name={`${index}_itemtype`}
                  id={`${index}_itemtype`}
                  className={inputClassNames}
                  defaultValue=''
                  value={item.type || ""}
                  onChange={(e) => {
                    dispatch({
                      type: "modifyItem",
                      index: index,
                      field: "type",
                      value: e.target.value,
                    });
                    dispatch({
                      type: "modifyItem",
                      index: index,
                      field: "data",
                      value: { id: "", name: "", price: 0 },
                    });
                    if (["custom", "voucher", "pif"].includes(e.target.value)) {
                      dispatch({
                        type: "modifyItem",
                        index: index,
                        field: "quantity",
                        value: 1,
                      });
                    }
                  }}>
                  <option value='' disabled className='text-gray-500'>
                    Select Item Type
                  </option>

                  {itemTypes.map((type) => (
                    <option
                      disabled={
                        (type === "custom" &&
                          lineItems.filter((i) => i.type === "custom")
                            .length === 1) ||
                        (type === "pif" &&
                          lineItems.filter((i) => i.type === "pif").length ===
                            1)
                      }
                      key={`${index}_option_${type}`}
                      value={type}
                      className='capitalize'>
                      {type}
                    </option>
                  ))}
                </select>
              </td>
              <td className='px-2 xl:px-6 py-2 whitespace-no-wrap leading-5'>
                {item.type === "product" ? (
                  <SingleProductSelector
                    id={`${index}_item`}
                    value={item.data}
                    setValue={(value) =>
                      dispatch({
                        type: "modifyItem",
                        index: index,
                        field: "data",
                        value: value as any,
                      })
                    }
                  />
                ) : item.type === "book" ? (
                  <SingleBookSelector
                    id={`${index}_item`}
                    value={item.data}
                    setValue={(value) =>
                      dispatch({
                        type: "modifyItem",
                        index: index,
                        field: "data",
                        value: value as any,
                      })
                    }
                  />
                ) : item.type === "custom" ? (
                  <StandardInput
                    type='text'
                    name='customItem'
                    id='customItm'
                    label=''
                    bind={{
                      value: item.data.name,
                      onChange: (e) =>
                        dispatch({
                          type: "modifyItem",
                          index: index,
                          field: "data",
                          value: {
                            ...item.data,
                            name: e.target.value as string,
                          },
                        }),
                    }}
                  />
                ) : item.type === "voucher" ? (
                  <StandardInput
                    type='email'
                    name='voucherRecipient'
                    id={`${index}_item`}
                    inputProps={{ placeholder: "recipient email" }}
                    label=''
                    bind={{
                      value: item.data.name,
                      onChange: (e) =>
                        dispatch({
                          type: "modifyItem",
                          index: index,
                          field: "data",
                          value: {
                            ...item.data,
                            name: e.target.value as string,
                          },
                        }),
                    }}
                  />
                ) : null}
              </td>
              <td className='px-2 xl:px-6 py-2 whitespace-no-wrap leading-5'>
                <span className='pl-3'>
                  {["custom", "voucher", "pif"].includes(item.type) ? (
                    <StandardInput
                      label=''
                      name={`${index}_item_price`}
                      id={`${index}_item_price`}
                      type='number'
                      bind={{
                        value: currency(item.data.price, {
                          fromCents: true,
                        }).value,
                        onChange: (e) =>
                          dispatch({
                            type: "modifyItem",
                            index: index,
                            field: "data",
                            value: {
                              ...item.data,
                              price: currency(e.target.value).intValue,
                            },
                          }),
                      }}
                    />
                  ) : (
                    currency(item.data.price, {
                      fromCents: true,
                    }).value
                  )}
                </span>
              </td>
              <td className='px-2 xl:px-6 py-2 whitespace-no-wrap leading-5 text-center'>
                {["custom", "voucher", "pif"].includes(item.type) ? (
                  <>{item.quantity}</>
                ) : (
                  <Input
                    name={`${index}_quantity`}
                    id={`${index}_quantity`}
                    value={item.quantity}
                    onChange={(e) =>
                      dispatch({
                        type: "modifyItem",
                        index: index,
                        field: "quantity",
                        value: e.target.value,
                      })
                    }
                  />
                )}
              </td>
              <td className='px-2 xl:px-6 py-2 whitespace-no-wrap leading-5 text-center'>
                {["custom", "voucher", "pif"].includes(item.type) ? (
                  "N/A"
                ) : (
                  <Input
                    name={`${index}_discount`}
                    id={`${index}_discount`}
                    type='string'
                    value={item.discount}
                    onChange={(e) =>
                      dispatch({
                        type: "modifyItem",
                        index: index,
                        field: "discount",
                        value: e.target.value,
                      })
                    }
                  />
                )}
              </td>
              <td className='px-2 xl:px-6 py-2 whitespace-no-wrap leading-5 text-center'>
                {item.totalPrice()}
              </td>
              <td className='py-2 px-2 xl:px-6 whitespace-no-wrap'>
                <button
                  type='button'
                  onClick={() =>
                    dispatch({
                      type: "removeItem",
                      index: index,
                    })
                  }
                  className='flex'>
                  <svg
                    className='w-4 h-4 text-gray-500 float-right hover:text-red-default'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 20 20'
                    fill='currentColor'>
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                      clipRule='evenodd'
                    />
                    <title>remove</title>
                  </svg>
                  <span className='sr-only'>Remove</span>
                </button>
              </td>
            </tr>
          ))}
          <tr>
            <td className='px-8 py-2 whitespace-no-wrap leading-5'>Shipping</td>
            <td className='px-2 xl:px-6 py-2 whitespace-no-wrap leading-5'>
              <ShippingSelector
                country={country.value}
                setValue={fulfillment.setValue}
                value={fulfillment.value}
              />
            </td>
            <td className='px-2 xl:px-6 py-2 whitespace-no-wrap leading-5'>
              {fulfillment.value.id === "custom" ? (
                <input
                  className={inputClassNames}
                  value={
                    currency(fulfillment.value.price, { fromCents: true }).value
                  }
                  onChange={(e) => {
                    fulfillment.setValue({
                      ...fulfillment.value,
                      price: currency(e.target.value).intValue,
                    });
                  }}
                />
              ) : (
                <>
                  {currency(fulfillment.value.price, { fromCents: true }).value}
                </>
              )}
            </td>
            <td></td>
            <td></td>
            <td className='px-2 xl:px-6 py-2 whitespace-no-wrap leading-5 text-center'>
              {currency(fulfillment.value.price, { fromCents: true }).value}
            </td>
            <td></td>
          </tr>
          <tr className='bg-gray-100'>
            <td
              className='px-2 xl:px-6 py-2 whitespace-no-wrap leading-5 text-right text-xs text-gray-800'
              colSpan={5}>
              <b>TOTAL</b>
            </td>
            <td className='px-2 xl:px-6 py-2 whitespace-no-wrap leading-5 text-center'>
              {calcTotal()}
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </fieldset>
  );
};
